import { Group, TextInput } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { notifications } from "@mantine/notifications"
import { IconPlus, IconSearch } from "@tabler/icons-react"
import { OnChangeFn, PaginationState } from "@tanstack/react-table"
import { CertificateCategory, CertificateTrigger } from "@tfg/db"
import { Button, Loader, Select } from "@tfg/ui"
import { useEffect } from "react"

import { AdminResponses } from "@tfg/types/endpoints/admin"
import {
  certifcatefiltersValidationType,
  certificateTypeCreateSchema,
  certificateTypeCreateValidationType,
} from "@tfg/types/schemas/admin"

import { FormFieldConfig } from "~/pages/Admin/components/TypesForm"

import { DrawerForm } from "../DrawerForm"

import { CertificatesTable } from "./CertificatesTable"
import { useCreateCertificateTypeMutation, useListStandardsQuery } from "./hook"

type Props = {
  certificates: AdminResponses.CertificateTypesWithTotal
  pagination: { pageIndex: number; pageSize: number }
  setPagination: OnChangeFn<PaginationState>
  setPageCount: (pageCount: number) => void
  pageCount: number
  setSearchCertificateType: (searchCertificateType: string) => void
  searchCertificateType: string
  isFetching: boolean
  filterValues: certifcatefiltersValidationType
  setFilterValues: (filterValues: certifcatefiltersValidationType) => void
}

export function CertifcateTypes({
  certificates,
  pagination,
  pageCount,
  setPagination,
  setSearchCertificateType,
  searchCertificateType,
  setPageCount,
  isFetching,
  filterValues,
  setFilterValues,
}: Props) {
  const [drawerOpen, drawerActions] = useDisclosure(false)
  const { mutate: createCertificateType } = useCreateCertificateTypeMutation({
    onSuccess: drawerActions.close,
    onError: () => {
      notifications.show({
        title: "Error",
        message: "Error updating Certificate Type",
        color: "red",
      })
    },
  })
  const { certificateTypes, totalCertificateTypes } = certificates
  useEffect(() => {
    if (totalCertificateTypes === 0) {
      setPageCount(0)
    } else if (totalCertificateTypes > 0) {
      setPageCount(Math.ceil(totalCertificateTypes / pagination.pageSize))
    }
  }, [totalCertificateTypes, pagination.pageSize, setPageCount])
  const { data: standardsList, isLoading } = useListStandardsQuery()
  const formattedOptions = standardsList
    ? standardsList.map((option) => ({
        value: option.id.toString(),
        label: option.name,
      }))
    : []

  const fields: FormFieldConfig[] = [
    {
      type: "text",
      name: "name",
      label: "Name",
      placeholder: "Name",
    },
    {
      type: "select",
      name: "category",
      label: "Category",
      placeholder: "Category",
      options: Object.values(CertificateCategory).map((option) => ({
        value: option,
        label: option,
      })),
    },
    {
      type: "select",
      name: "trigger",
      label: "Trigger",
      placeholder: "trigger",
      options: Object.values(CertificateTrigger).map((option) => ({
        value: option,
        label: option,
      })),
    },
    {
      type: "multiselect",
      name: "standard",
      label: "Standards",
      placeholder: "Standards",
      options: formattedOptions,
    },
  ]
  return (
    <>
      <Group justify="space-between" gap="sm" py="sm">
        <Group gap="sm">
          <TextInput
            size="xs"
            w={200}
            autoFocus
            placeholder="Search certificate types"
            leftSection={<IconSearch size={16} />}
            value={searchCertificateType}
            onChange={(event) =>
              setSearchCertificateType(event.currentTarget.value)
            }
            styles={{
              input: {
                backgroundColor: "white",
              },
            }}
            rightSection={isFetching && <Loader size={16} />}
          />
          <Select
            data={Object.values(CertificateTrigger)}
            w={200}
            size="xs"
            value={filterValues.trigger}
            searchable
            onChange={(value) =>
              setFilterValues({
                ...filterValues,
                trigger:
                  value && value in CertificateTrigger
                    ? (value as CertificateTrigger)
                    : undefined,
              })
            }
            placeholder="Trigger"
            styles={{
              input: {
                backgroundColor: "white",
              },
            }}
          />
        </Group>
        <Button onClick={() => drawerActions.open()} icon={<IconPlus />}>
          New Certificate Type
        </Button>
      </Group>
      {drawerOpen ? (
        isLoading ? null : (
          <DrawerForm<certificateTypeCreateValidationType>
            fields={fields}
            initialValues={{
              name: "",
              standard: [],
              category: "TRANSACTION",
              trigger: "LAST_SUPPLIER",
              attributes: undefined,
            }}
            drawerActions={drawerActions}
            schema={certificateTypeCreateSchema}
            mutate={createCertificateType}
          />
        )
      ) : null}
      {isLoading ? (
        <Loader />
      ) : (
        <CertificatesTable
          certificateTypes={certificateTypes}
          standardsList={standardsList || []}
          setPagination={setPagination}
          pageCount={pageCount}
          pagination={pagination}
          totalCertificateTypes={totalCertificateTypes}
        />
      )}
    </>
  )
}
